/* System defined core library */
import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { MetricApiService } from '../../shared/services/metric-api.service';
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { AppointmentService } from '../../shared/services/appointment/appointment.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';

/* User defined enums/constants */
import { ComponentType, LogLevel } from '../../../shared/enums/scheduling.enum';
import { BatteryConfig } from '../../../shared/models/scheduling/batteryConfig.model';
import { voucherPaymentCodes } from '../../../shared/constants/scheduling.constant';

@Component({
  selector: 'app-reschedule',
  templateUrl: './reschedule.component.html',
  styleUrls: ['./reschedule.component.scss']
})
export class RescheduleComponent {

  /* User defined private/public variables */
  public appointmentDetails: AppointmentDetail[] = [];
  public apptDetails: AppointmentDetail[] = [];
  public booking: Booking;
  public candidate: Candidate;
  public invalidScheduleMethod: boolean = false;
  public invalidReschedulePolicy: boolean = false;
  public isNextButtonEnabled: boolean = false;
  public skipReschedule = false
  public isRescheduleAllowed = true;
  public isValid: boolean = false;
  public pathLock = false;
  public targetFlow: string = 'Reschedule';
  public overLapEndDate: any;
  public overLapStartDate: any;
  public rescheduleErrorMessage: string;
  public batteryConfig: BatteryConfig;

  /* Service subscriptions */
  appointmentDetailData$: Subscription;
  booking$: Subscription;
  candidateId$: Subscription;
  skipReschedule$: Subscription;
  overLapStartDate$: Subscription;
  overLapEndDate$: Subscription;
  translateLanguage$: Subscription;
  batteryConfig$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private appointmentService: AppointmentService,
    private metricApiService: MetricApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService,
    private utilityService: UtilityService
  ) {
    this.appointmentDetailData$ = selectedExamDetailService.scheduledExams.subscribe(
      scheduledExams => {
        this.apptDetails = scheduledExams;
      });

    this.booking$ = selectedExamDetailService.booking.subscribe(
      booking => {
        this.booking = booking;
      });

    this.candidateId$ = selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
      });

    this.skipReschedule$ = selectedExamDetailService.skipReschedule.subscribe(
      skipReschedule => {
        this.skipReschedule = skipReschedule;
      });

    this.overLapEndDate$ = selectedExamDetailService.overlapsToDate.subscribe(
      endDate => {
        this.overLapEndDate = endDate;
      });

    this.overLapStartDate$ = selectedExamDetailService.overlapsFromDate.subscribe(
      startDate => {
        this.overLapStartDate = startDate;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.batteryConfig$ = selectedExamDetailService.batteryConfig.subscribe(
      batteryConfig => {
        this.batteryConfig = batteryConfig;
      });
  }

  /* Initialize the component & call required methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.RescheduleComponent)) { return; }
    this.pathLock = this.selectedExamDetailService.setReschedulePathLock;
  }

  /* Move back a page. */
  goBack(): void {
    this.routingService.backwardNavigation(ComponentType.RescheduleComponent);
  }

  /* disable next button on the basis of local variable value */
  isDisable(): boolean {
    return !this.isNextButtonEnabled || !this.isValid || this.selectedExamDetailService.programNotPublished || !this.isRescheduleAllowed;
  }

  /* Move on next screen to select location */
  submit(event: any): void {
    this.apptDetails = this.utilityService.dateSortAsc(this.apptDetails);
    if (this.apptDetails.length) {
      // to grab first scheduled
      this.selectedExamDetailService.setScheduledSlot(
        this.apptDetails[0].appointmentSlot);
    }
    if (this.appointmentDetails.length > 0) {
      this.appointmentDetails.forEach(appointment => {
        appointment.appointmentSlot = new AppointmentSlot();

        if (appointment.exam.sessions && appointment.exam.sessions.length) {
          appointment.exam.sessions.forEach((s) => {
            if (s.selected) {
              s.slot = null;
              s.selectedDate = null;
            }
          })
        }
      });
      this.selectedExamDetailService.setSelectedAppointmentDetails(this.appointmentDetails);
      this.selectedExamDetailService.setClient(this.booking.client);
      this.selectedExamDetailService.selectedAppointmentIndex = 0;
      this.selectedExamDetailService.appointmentCountForInfo.next(0);
      this.selectedExamDetailService.setProgram(this.appointmentDetails[0].program);
      this.selectedExamDetailService.setExam(this.appointmentDetails[0].exam);
      this.selectedExamDetailService.setPostedConfirmationList([]);


      this.routingService.rescheduleFwdNavigation(ComponentType.RescheduleComponent);
    }
  }

  /* Enable next button if atleast one appointment is selected */
  enableNextButton(isAppointmentSelected: boolean): void {
    this.isNextButtonEnabled = isAppointmentSelected;
  }

  /* Emitter to set the selected appointments in shared service  */
  setSelectedAppointments(appointmentData: AppointmentDetail[]): void {
    this.appointmentDetails = appointmentData;
    this.selectedExamDetailService.setSelectedAppointmentDetails(appointmentData);
    if (!!this.appointmentDetails && this.appointmentDetails.length) {
      this.getConsents(this.appointmentDetails[0].id);
    }
  }

  isAllExamValidEmit($event: any) {
    this.isValid = $event;
    if (this.appointmentDetails.length) {
      const voucherPayment = this.appointmentDetails[0].payments.filter(p => voucherPaymentCodes.indexOf(p.paymentType.toLowerCase()) !== -1);
      if (voucherPayment.length) {
        this.validateVoucher();
      }
    }
  }

  rescheduleNotAllowedPolicy($event: any) {
    this.invalidReschedulePolicy = $event['error'];
    this.rescheduleErrorMessage = $event['message'];
  }

  isScheduleMethodValid($event: any) {
    this.invalidScheduleMethod = $event;
    this.isValid = !$event;
  }

  ssoClientEmit($event: boolean) {
    if ($event) {
      this.metricApiService.log(LogLevel.Debug, 'User attempted to reschedule SSO appointment without SSO credentials: User Redirected to error page.');
      this.routingService.invalidNavigation();
    }
  }

  /* used to get biometric consent saved on schedule */
  getConsents(confirmationId: string): void {
    this.schedulingApiService.getConsents(confirmationId).subscribe((resp) => {
      if (!!resp.Results[0].Consent) {
        const consent = resp.Results[0].Consent;
        if (!!consent.biometric && consent.biometric.length > 0) {
          if (consent.biometric[0] === 'True') {
            this.selectedExamDetailService.isBiometricConsentSavedInSchedule = true;
          } else {
            this.selectedExamDetailService.isBiometricConsentSavedInSchedule = false;
          }
        }
      }

    });
  }

  getPathLock() {
    return this.selectedExamDetailService.setReschedulePathLock;
  }

  validateVoucher() {
    this.appointmentService.validateVoucherPayment
      (this.appointmentDetails, this.candidate, this.overLapStartDate, this.overLapEndDate).subscribe((isAllowed: boolean) => {
        this.isRescheduleAllowed = isAllowed;
      });
  }
}
